import Body from 'chameleon/ui-stack/ui/page/body';
import useNavigation from 'chameleon/ui-stack/hooks/useNavigation';
import useFetchStudentAssignments from 'chameleon/ui-stack/hooks/use-assignments-fetch/student';
import StudentAssignmentsTable from 'chameleon/ui-stack/ui/assessments/student-assessments-table';
import { displayDigitalAssessmentInfoModal } from 'legacy/src/components/assessment/DigitalAssessmentInfoModal/utils';
import DigitalAssessmentInfoModal from 'legacy/src/components/assessment/DigitalAssessmentInfoModal';

import {
  timeLeftUntilCbSessionTimeout,
  timeLeftUntilCBSessionExpire,
} from 'legacy-src/components/auth/utils';
import hasOptionalPractice from 'legacy-src/utility/helpers/hasOptionalPractice';
import { connect } from 'react-redux';
import isMobileDevice from 'legacy-src/utility/isMobile';
import {
  openSecureBrowserLink,
  SECURE_BROWSER_STATUS_CHECKING,
  SECURE_BROWSER_STATUS_FOUND,
  SECURE_BROWSER_STATUS_MISSING,
} from 'legacy-src/components/apstudent/handlers/Assignments/lockdownbrowser';
import SecureBrowserBanner from 'legacy-src/components/apstudent/handlers/Assignments/SecureBrowserBanner';
import CSPBanner from 'chameleon/legacy/src/components/apstudent/handlers/Assignments/CSPBanner';
import InformationalModal from 'chameleon/ui-stack/ui/modals/informational-modal';
import { MOBILE_LDB_MESSAGE } from 'legacy-src/utility/AssignmentOpen';
import MissingSecureBrowserBanner from 'legacy-src/components/apstudent/handlers/Assignments/MissingSecureBrowserBanner';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import TimedAssignmentWarningModal from 'chameleon/ui-stack/ui/assignments/timed-assignment-warning-modal';
import FullTextSearch from 'legacy-src/components/assessment/Filters/components/FullTextSearch';
import { SearchGuideModalType } from 'chameleon/legacy/src/components/assessment/Filters/components/SearchGuideModal/types';
import { MobileMedia } from 'legacy-src/utility/ResponsiveView';
import { get, isEqual } from 'lodash';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import ResourceTypeFilter from './ResourceTypeFilter';
import useLocalStorage from 'chameleon/ui-stack/ui/base/hooks/use-local-storage';

const GET_FEEDBACK = 'GET FEEDBACK';
export const NO_DATA_TEXT = 'No assignments to display.';

const StudentAssignmentsContainer = ({
  router,
  featureFlag,
  isImpersonating,
  levityUseCreateTime,
  studentPracticeProblems,
  pdfAssignmentsFF,
  bluebookUIFF,
  userSubjects,
  isStudent,
  subjectData,
  allowHardTimerFF,
  afamEp26FullLaunchP1FF,
  ...props
}) => {
  // Some tables are memoized and the router information is inside a closure,
  // so we need to find an alternative way to keep it updated.
  const routerRef = useRef(router);
  const { params = {}, userData } = props || {};
  const { me } = userData || {};

  const [data, setData] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [showTimedAssignmentModal, setShowTimedAssignmentModal] =
    useState(false);

  const [
    shouldDisplayDigitalAssessmentInfoModal,
    setShouldDisplayDigitalAssessmentInfoModal,
  ] = useState(false);
  const [digitalAssessmentInfoModalOpen, setDigitalAssessmentInfoModalOpen] =
    useState(false);

  const { queryParams } = useNavigation({
    router,
  });
  const {
    status = 'assigned',
    type: resourceType = '',
    labels,
    searchTerm,
  } = queryParams || {};
  const { subject } = params;
  const [getStudentAssignments] = useFetchStudentAssignments();
  const assignmentRef = useRef();
  const assignmentClickedEventRef = useRef();
  const { initId: userId } = me || {};

  const loadAssignments = () => {
    const fetchProps = {
      subject,
      status,
      resourceType,
      searchTerm,
      setData,
      setLoading,
      isImpersonating,
      params,
      labels,
    };
    getStudentAssignments({ ...fetchProps });
  };

  useEffect(() => {
    loadAssignments();
  }, [status, searchTerm, labels]);

  useEffect(() => {
    routerRef.current = router;
  }, [router]);

  const isLdbEnabled = featureFlag.secure_browser;

  const onReviewClick = (e, assignment) => {
    e.preventDefault();
    e.stopPropagation();
    const { location } = routerRef.current;
    const isSpp = get(assignment, 'item.is_spp', false);
    const pathname = isSpp
      ? `/${subject}/assessments/spp/review/${assignment.id}`
      : `/${subject}/assessments/results/${assignment.id}`;
    const returnPath = `${location.pathname}${location.search}`;
    const state = isSpp ? { returnPath } : {};
    router.push({ pathname, state });
  };

  /**
   * Navigates to a given assignment by its id
   * @param {Object} assignment
   **/
  const beginAssignment = (event, assignment) => {
    const { location } = routerRef.current;
    event.preventDefault();
    const { headline, is_secure } = assignment;
    if (is_secure && isLdbEnabled && headline !== GET_FEEDBACK) {
      // PREAP-1483: if we require a secure browser url, the url provided is expected to be an ldb1 protocol. We'll need to check
      // that this host actually has it installed.
      if (isMobileDevice()) {
        setSecureAssessmentOnMobile(true);
      } else {
        openSecureBrowserLink(assignment.secure_link, onBrowserStatusChange);
        triggerDecorator();
      }
    } else {
      const isSPP = get(assignment, 'item.is_spp');
      let pathname = `/${subject}/assessments/assignments/${assignment.id}`;

      triggerDecorator();
      const returnPath = `${location.pathname}${location.search}`;

      if (isSPP) {
        pathname = `/${subject}/assessments/spp/${assignment.id}`;
      }
      router.push({
        pathname,
        state: { returnPath },
      });
    }
  };

  // eslint-disable-next-line class-methods-use-this
  const triggerDecorator = () =>
    // Return true to get the telemetry decorator triggered
    true;

  /**
   * Click handler for the Begin button for a timed assignment
   * @param {Object} assignment The assignment object
   */
  const onTimedAssessmentClick = (event, assignment) => {
    const checkCBSessionFlag =
      featureFlag.cb_session_expiration_check_before_take_assignment;
    const useCreateTimeOrExpireTime = levityUseCreateTime
      ? timeLeftUntilCbSessionTimeout()
      : timeLeftUntilCBSessionExpire();
    const cbSessionExpirationOverride =
      featureFlag.cb_session_expiration_override;
    // hard override only used for debugging, should remain off, see FY-12447
    const expirationTimeDiff =
      cbSessionExpirationOverride || useCreateTimeOrExpireTime;
    const { timer } = assignment;
    const expiringSession = expirationTimeDiff <= timer;

    if (checkCBSessionFlag && expiringSession) {
      beginAssignment(event, assignment);
      // if user has no expiration time, render the regular timed assessment warning
      // if no expiration time, we assume it's a direct login and render the regular timed assessment warning
      // if expire time greater than assignment timer, render the regular timed assessment warning
    } else if (checkCBSessionFlag) {
      event.persist();
      assignmentRef.current = assignment;
      setShowTimedAssignmentModal(true);
    } else {
      beginAssignment(event, assignment);
    }
  };

  const openReadinessCheck = (url) => {
    if (isMobileDevice()) {
      setSecureAssessmentOnMobile(true);
    } else {
      openSecureBrowserLink(url, onBrowserStatusChange);
    }
  };

  const onBrowserStatusChange = (status) => {
    switch (status) {
      case SECURE_BROWSER_STATUS_FOUND:
        break;
      case SECURE_BROWSER_STATUS_CHECKING:
        break;
      case SECURE_BROWSER_STATUS_MISSING:
        setShowMissingSecureBrowserBanner(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setMissingSecureBrowserTimeoutId(
          setTimeout(() => {
            setShowMissingSecureBrowserBanner(false);
          }, 180000),
        );
        break;
      default:
        break;
    }
  };

  const [secureAssessmentOnMobile, setSecureAssessmentOnMobile] =
    useState(false);
  const [showMissingSecureBrowserBanner, setShowMissingSecureBrowserBanner] =
    useState(false);
  const [missingSecureBrowserTimeoutId, setMissingSecureBrowserTimeoutId] =
    useState(null);

  useEffect(
    () => () => {
      if (missingSecureBrowserTimeoutId) {
        clearTimeout(missingSecureBrowserTimeoutId);
      }
    },
    [missingSecureBrowserTimeoutId],
  );

  const handleIsStudentPracticeBlankMessageAndOrder = () => {
    const { location } = routerRef.current;
    const { isStudentPracticeBlankMessageAndOrder } = props;
    return (
      isStudentPracticeBlankMessageAndOrder &&
      hasOptionalPractice(location.pathname)
    );
  };

  const {
    assignments,
    has_secure_assignments: hasSecureAssignments,
    readiness_check_url: readinessCheckURL,
  } = data || {};

  const filteredAssignments = resourceType
    ? assignments?.filter((a) => a.type === resourceType)
    : assignments;

  const requireSecureBrowser = hasSecureAssignments;
  const showSecureBrowserBanner = isLdbEnabled && requireSecureBrowser;
  const showCSPBanner = status === 'completed';

  const onAssignmentClick = (event, assignment) => {
    event.stopPropagation();
    const isTimedAssignment = get(assignment, 'timer', null);
    const notStarted = get(assignment, 'progress.not_started', null);
    const isSpp = get(assignment, 'item.is_spp', false);

    if (isTimedAssignment && notStarted && !isSpp) {
      onTimedAssessmentClick(event, assignment);
    } else {
      beginAssignment(event, assignment);
    }
  };

  const onModalSuccess = (event) => {
    beginAssignment(event, assignmentRef.current);
    setShowTimedAssignmentModal(false);
    setDigitalAssessmentInfoModalOpen(false); // close DigitalAssessmentInfoModal if open
  };

  const onModalClose = () => {
    setShowTimedAssignmentModal(false);
  };

  const isMobile = MobileMedia.matches;

  const renderDigitalAssessmentInfoModal = (event, assignment) => {
    event.persist();
    if (assignment.actions[0] !== 'begin') {
      // continue the usual flow
      return onAssignmentClick(event, assignment);
    }
    const getDisplayDigitalAssessmentInfoModal =
      displayDigitalAssessmentInfoModal({
        subject: router.params.subject,
        bluebookUIFF,
        userPreferences: me.preferences,
        userSubjects,
        assesmentType: assignment.type,
        isStudent,
        subjectData,
        isCoTaught: false,
      });
    assignmentClickedEventRef.current = event;
    assignmentRef.current = assignment;
    setShouldDisplayDigitalAssessmentInfoModal(
      getDisplayDigitalAssessmentInfoModal && assignment.actions[0] === 'begin',
    );
    setDigitalAssessmentInfoModalOpen(true);
    if (!getDisplayDigitalAssessmentInfoModal) {
      onAssignmentClick(event, assignment); // continue the normal flow
    }
  };
  const [saveItemToLocalStorage, , getSavedSortOrder] = useLocalStorage({
    key: `sortBy`,
    itemKey: `${userId}-${subject}-assignment`,
    index: 1,
  });

  const currentSortBy = useRef(() => {
    const savedSortOrders = JSON.parse(getSavedSortOrder() || '{}');
    return savedSortOrders[status] || [];
  });

  const saveSortOrder = (newSortBy) => {
    if (newSortBy.length && !isEqual(newSortBy, currentSortBy.current)) {
      currentSortBy.current = newSortBy;
      const savedSortOrders = JSON.parse(getSavedSortOrder() || '{}');
      savedSortOrders[status] = newSortBy;
      saveItemToLocalStorage(JSON.stringify(savedSortOrders));
    }
  };

  useEffect(() => {
    const savedSortOrders = JSON.parse(getSavedSortOrder() || '{}');
    currentSortBy.current = savedSortOrders[status] || [];
  }, [currentSortBy.current]);

  return (
    <Fragment>
      <Body>
        {showTimedAssignmentModal ? (
          <TimedAssignmentWarningModal
            onSuccess={onModalSuccess}
            onClose={onModalClose}
            timer={assignmentRef.current.timer}
            isHardTimer={assignmentRef.current.is_hard_timer}
            allowHardTimerFF={allowHardTimerFF}
          />
        ) : null}
        {shouldDisplayDigitalAssessmentInfoModal &&
          digitalAssessmentInfoModalOpen && (
            <DigitalAssessmentInfoModal
              onClose={() => setDigitalAssessmentInfoModalOpen(false)}
              onSubmit={() =>
                onAssignmentClick(
                  assignmentClickedEventRef.current,
                  assignmentRef.current,
                )
              }
            />
          )}
        {showSecureBrowserBanner && (
          <SecureBrowserBanner
            ipadLdbMessageFlag={featureFlag.iPad_LDB_message}
            readinessCheckURL={readinessCheckURL}
            openReadinessCheck={openReadinessCheck}
          />
        )}
        {showCSPBanner && <CSPBanner subject={subject} path="assignments" />}
        {showSecureBrowserBanner && showMissingSecureBrowserBanner && (
          <MissingSecureBrowserBanner />
        )}
        <ResourceTypeFilter
          router={router}
          studentPracticeProblems={studentPracticeProblems}
          pdfAssignmentsFF={pdfAssignmentsFF}
          afamEp26FullLaunchP1FF={afamEp26FullLaunchP1FF}
          subject={subject}
          userSubjects={userData?.studentSubjects}
          isStudent={isStudent}
        />
        <FullTextSearch
          showTipsButton={!isMobile}
          tipsModalPage={SearchGuideModalType.StudentAssignments}
          className="max-w-128 mt-6 flex-1 mobile:px-8.4 mobile:max-w-full mobile:mt-4"
          inputContainerClassName="w-auto"
          inputPlaceholder="Search assignment title"
          searchTerm={queryParams?.searchTerm || ''}
          component={{
            props: {
              location: router.location,
              params: router.params,
              router,
            },
          }}
        />
        {!isLoading ? (
          <StudentAssignmentsTable
            studentId={me.initId}
            featureFlag={featureFlag}
            onAssignmentClick={renderDigitalAssessmentInfoModal}
            onReviewClick={onReviewClick}
            handleIsStudentPracticeBlankMessageAndOrder={
              handleIsStudentPracticeBlankMessageAndOrder
            }
            loadAssignments={loadAssignments}
            data={filteredAssignments}
            noDataText={NO_DATA_TEXT}
            initialSortBy={currentSortBy.current || []}
            onSortChange={saveSortOrder}
          />
        ) : (
          <LoadingSpinner />
        )}
        {secureAssessmentOnMobile && (
          <InformationalModal
            title="Secure Assessment Not Supported"
            onClose={() => setSecureAssessmentOnMobile(false)}
          >
            <p>{MOBILE_LDB_MESSAGE}</p>
          </InformationalModal>
        )}
      </Body>
    </Fragment>
  );
};

export default connect((state) => {
  const isReadOnly = state.impersonating && state.impersonating.isReadOnly;
  const apTeacherImpersonatesStudent =
    state.featureFlag.ap_teacher_impersonates_student;
  const {
    student_practice_problems,
    my_assignments_pdf: pdfAssignmentsFF,
    spp_filter_by_subject,
    spp_filter_by_school,
    bluebook_ui: bluebookUIFF,
    updated_timer_controls: allowHardTimerFF,
    afam_ep26_full_launch_p1: afamEp26FullLaunchP1FF,
  } = state.featureFlag;
  const {
    userSubjects,
    userData: { isStudent },
  } = state;
  return {
    isStudentPracticeBlankMessageAndOrder:
      state.featureFlag.student_practice_blank_message_and_order,
    featureFlag: state.featureFlag,
    levityUseCreateTime: state.featureFlag.levity_use_createtime,
    subjectData: state.subject.data,
    isImpersonating:
      Boolean(isReadOnly && apTeacherImpersonatesStudent) ||
      window.localStorage.getItem('isReadOnly') === 'true',
    studentPracticeProblems:
      student_practice_problems ||
      (spp_filter_by_subject && spp_filter_by_school),
    pdfAssignmentsFF,
    bluebookUIFF,
    userSubjects,
    isStudent,
    allowHardTimerFF,
    afamEp26FullLaunchP1FF,
  };
})(StudentAssignmentsContainer);
